import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { bulkUploadService } from '../../../services/bulkUploadService';
import { courseService } from '../../../services/courseService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const BulkCourseUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const { userProfile } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Dynamic catalog values loaded from superadmin
  const [validCategories, setValidCategories] = useState([]);
  const [validLevels, setValidLevels] = useState([]);

  // Default fallback values
  const defaultCategories = [
    'engineering', 'business & management', 'medical', 'design', 'law',
    'hospitality & travel', 'animation', 'mass communication & media',
    'information technology', 'humanities & social sciences', 'arts', 'science',
    'fine arts & performing arts', 'architecture', 'accounting', 'commerce',
    'finance', 'banking', 'fashion design', 'literature', 'research'
  ];
  const defaultLevels = ['diploma', 'undergraduate', 'post graduate', 'integrated courses', 'post graduate diploma', 'doctoral'];

  useEffect(() => {
    if (!isOpen) return;
    const fetchCatalog = async () => {
      try {
        const { data, error: catalogError } = await supabase?.from('global_course_catalog')?.select('name, type')?.eq('is_active', true);

        if (catalogError) throw catalogError;

        const cats = (data || [])?.filter(item => item?.type === 'category')?.map(item => item?.name?.toLowerCase()?.trim());
        const lvls = (data || [])?.filter(item => item?.type === 'level')?.map(item => item?.name?.toLowerCase()?.trim());

        setValidCategories(cats?.length > 0 ? cats : defaultCategories);
        setValidLevels(lvls?.length > 0 ? lvls : defaultLevels);
      } catch (err) {
        console.error('Error fetching catalog for validation:', err);
        setValidCategories(defaultCategories);
        setValidLevels(defaultLevels);
      }
    };
    fetchCatalog();
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      // Validate file type
      const fileExtension = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
      if (fileExtension !== 'csv') {
        setError('Please upload a CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResults(null);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'course_name',
      'category',
      'course_type',
      'specialization',
      'level',
      'duration',
      'fees',
      'show_fee',
      'eligibility',
      'admission_process',
      'placement_stats',
      'accreditation'
    ];

    const sampleData = [
      {
        course_name: 'Bachelor of Technology in Computer Science',
        category: 'engineering',
        course_type: 'Full Time',
        specialization: 'Computer Science',
        level: 'undergraduate',
        duration: '4 Years',
        fees: '500000',
        show_fee: 'true',
        eligibility: '10+2 with Physics, Chemistry, Mathematics',
        admission_process: 'JEE Main / State Entrance Exam',
        placement_stats: 'Average Package: 8 LPA, Highest: 25 LPA',
        accreditation: 'AICTE Approved, NBA Accredited'
      },
      {
        course_name: 'Master of Business Administration',
        category: 'management',
        course_type: 'Full Time',
        specialization: 'Finance & Marketing',
        level: 'postgraduate',
        duration: '2 Years',
        fees: '800000',
        show_fee: 'true',
        eligibility: 'Bachelor Degree with minimum 50% marks',
        admission_process: 'CAT / MAT / CMAT',
        placement_stats: 'Average Package: 12 LPA',
        accreditation: 'AICTE Approved'
      }
    ];

    const csvContent = [
      headers?.join(','),
      ...sampleData?.map(row => 
        headers?.map(header => {
          const value = row?.[header] || '';
          // Escape values containing commas or quotes
          if (value?.includes(',') || value?.includes('"') || value?.includes('\n')) {
            return `"${value?.replace(/"/g, '""')}"`;
          }
          return value;
        })?.join(',')
      )
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'course_upload_template.csv';
    link?.click();
  };

  const validateCourseData = (data) => {
    const errors = [];
    const validRows = [];

    // Required fields
    const requiredFields = ['course_name', 'category', 'course_type', 'level', 'duration'];

    // Valid category values
    const validCategoriesList = validCategories?.length > 0 ? validCategories : defaultCategories;

    // Valid level values
    const validLevelsList = validLevels?.length > 0 ? validLevels : defaultLevels;

    if (data?.length === 0) {
      return { valid: false, errors: ['CSV file is empty'], validRows: [] };
    }

    const headers = Object.keys(data?.[0] || {});
    const missingHeaders = requiredFields?.filter(field => !headers?.includes(field));

    if (missingHeaders?.length > 0) {
      return {
        valid: false,
        errors: [`Missing required columns: ${missingHeaders?.join(', ')}`],
        validRows: []
      };
    }

    // Validate each row
    data?.forEach((row, index) => {
      const rowErrors = [];
      const rowNumber = index + 2; // +2 because index starts at 0 and row 1 is header

      // Validate required fields
      requiredFields?.forEach(field => {
        if (!row?.[field] || row?.[field]?.trim() === '') {
          rowErrors?.push({
            row: rowNumber,
            column: field,
            value: '',
            message: `Column "${field?.replace(/_/g, ' ')}" is required but empty`
          });
        }
      });

      // Validate category (case-insensitive, trimmed)
      if (row?.category && row?.category?.trim() !== '') {
        const normalizedCategory = row?.category?.toLowerCase()?.trim();
        if (!validCategoriesList?.includes(normalizedCategory)) {
          rowErrors?.push({
            row: rowNumber,
            column: 'category',
            value: row?.category?.trim(),
            message: `"${row?.category?.trim()}" is not a valid category. Valid: ${validCategoriesList?.map(c => c?.split(' ')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' '))?.join(', ')}`
          });
        }
      }

      // Validate level (case-insensitive, trimmed)
      if (row?.level && row?.level?.trim() !== '') {
        const normalizedLevel = row?.level?.toLowerCase()?.trim();
        if (!validLevelsList?.includes(normalizedLevel)) {
          rowErrors?.push({
            row: rowNumber,
            column: 'level',
            value: row?.level?.trim(),
            message: `"${row?.level?.trim()}" is not a valid level. Valid: Diploma, Undergraduate, Post Graduate, Integrated Courses, Post Graduate Diploma, Doctoral`
          });
        }
      }

      // Validate fees (if provided and show_fee is true)
      if (row?.fees && row?.fees?.trim() !== '') {
        const feeValue = parseFloat(row?.fees);
        if (isNaN(feeValue) || feeValue < 0) {
          rowErrors?.push({
            row: rowNumber,
            column: 'fees',
            value: row?.fees?.trim(),
            message: `"${row?.fees?.trim()}" is not a valid fee amount (must be a positive number)`
          });
        }
      }

      // Validate show_fee
      if (row?.show_fee && !['true', 'false', '']?.includes(row?.show_fee?.toLowerCase()?.trim())) {
        rowErrors?.push({
          row: rowNumber,
          column: 'show_fee',
          value: row?.show_fee?.trim(),
          message: `"${row?.show_fee?.trim()}" is invalid — show_fee must be 'true' or 'false'`
        });
      }

      if (rowErrors?.length > 0) {
        errors?.push(...rowErrors);
      } else {
        validRows?.push({
          ...row,
          rowNumber
        });
      }
    });

    return {
      valid: errors?.length === 0,
      errors,
      validRows,
      totalRows: data?.length,
      validCount: validRows?.length,
      errorCount: errors?.length
    };
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    const instituteId = userProfile?.institute_profiles?.[0]?.id;
    if (!instituteId) {
      setError('Institute profile not found');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setProgress({ current: 0, total: 0, percentage: 0 });

      // Parse CSV
      const parseResult = await bulkUploadService?.parseCSVFile(file);
      
      if (parseResult?.errors?.length > 0) {
        throw new Error(`CSV parsing failed: ${parseResult?.errors?.[0]?.message}`);
      }

      // Validate data
      const validation = validateCourseData(parseResult?.data);
      
      if (!validation?.valid) {
        const errorSummary = validation?.errors?.slice(0, 5)?.map(e =>
          `Row ${e?.row}, Column "${e?.column}": ${e?.message}`
        )?.join('; ');
        setError(`Validation failed: ${errorSummary}${validation?.errors?.length > 5 ? ` ... and ${validation?.errors?.length - 5} more` : ''}`);
        setResults({
          successful: [],
          failed: validation?.errors?.map(err => ({
            rowNumber: err?.row,
            column: err?.column,
            value: err?.value,
            error: err?.message
          })),
          totalProcessed: 0,
          successCount: 0,
          failureCount: validation?.errors?.length
        });
        return;
      }

      // Bulk create courses
      const uploadResults = {
        successful: [],
        failed: []
      };

      const total = validation?.validRows?.length;
      setProgress({ current: 0, total, percentage: 0 });

      for (let i = 0; i < validation?.validRows?.length; i++) {
        const row = validation?.validRows?.[i];

        try {
          const courseData = {
            instituteId,
            courseName: row?.course_name?.trim(),
            category: row?.category?.toLowerCase()?.trim(),
            courseType: row?.course_type?.trim(),
            specialization: row?.specialization?.trim() || null,
            level: row?.level?.toLowerCase()?.trim(),
            duration: row?.duration?.trim(),
            fees: row?.fees?.trim() ? parseFloat(row?.fees) : null,
            showFee: row?.show_fee?.toLowerCase()?.trim() === 'false' ? false : true,
            eligibility: row?.eligibility?.trim() || null,
            admissionProcess: row?.admission_process?.trim() || null,
            placementStats: row?.placement_stats?.trim() || null,
            accreditation: row?.accreditation?.trim() || null
          };

          await courseService?.createCourse(courseData);
          
          uploadResults?.successful?.push({
            rowNumber: row?.rowNumber,
            courseName: courseData?.courseName
          });
        } catch (error) {
          uploadResults?.failed?.push({
            rowNumber: row?.rowNumber,
            courseName: row?.course_name,
            error: error?.message || 'Unknown error'
          });
        }

        // Update progress
        setProgress({
          current: i + 1,
          total,
          percentage: Math.round(((i + 1) / total) * 100)
        });
      }

      setResults({
        successful: uploadResults?.successful,
        failed: uploadResults?.failed,
        totalProcessed: total,
        successCount: uploadResults?.successful?.length,
        failureCount: uploadResults?.failed?.length
      });

      if (uploadResults?.successful?.length > 0) {
        onSuccess?.();
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      setError(error?.message || 'Failed to upload courses');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadResults = () => {
    if (!results) return;

    const headers = ['Row Number', 'Course Name', 'Status', 'Error Message'];
    const rows = [
      ...results?.successful?.map(item => ([
        item?.rowNumber,
        item?.courseName,
        'Success',
        ''
      ])),
      ...results?.failed?.map(item => ([
        item?.rowNumber,
        item?.courseName || 'N/A',
        'Failed',
        item?.error
      ]))
    ];

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => 
        row?.map(cell => {
          const value = String(cell || '');
          if (value?.includes(',') || value?.includes('"') || value?.includes('\n')) {
            return `"${value?.replace(/"/g, '""')}"`;
          }
          return value;
        })?.join(',')
      )
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `course_upload_results_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    link?.click();
  };

  const handleClose = () => {
    setFile(null);
    setProgress(null);
    setResults(null);
    setError(null);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bulk Upload Courses</h2>
              <p className="text-sm text-white/80">Upload multiple courses using CSV file</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">Upload Instructions</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Download the CSV template and fill in course details</li>
                  <li>• Required fields: course_name, category, course_type, level, duration</li>
                  <li>• Valid categories: Engineering, Business & Management, Medical, Design, Law, Hospitality & Travel, Animation, Mass Communication & Media, Information Technology, Humanities & Social Sciences, Arts, Science, Fine Arts & Performing Arts, Architecture, Accounting, Commerce, Finance, Banking, Fashion Design, Literature, Research</li>
                  <li>• Valid levels: Diploma, Undergraduate, Post Graduate, Integrated Courses, Post Graduate Diploma, Doctoral</li>
                  <li>• Fees are optional. Set show_fee to 'false' to hide fees</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Download Template */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="FileSpreadsheet" size={24} className="text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">CSV Template</h4>
                <p className="text-sm text-muted-foreground">Download template with sample data</p>
              </div>
            </div>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleDownloadTemplate}
              disabled={uploading}
            >
              Download
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-foreground">
              Upload CSV File
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Upload" size={32} className="text-primary" />
                </div>
                {file ? (
                  <div className="space-y-1">
                    <p className="font-semibold text-foreground">{file?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file?.size / 1024)?.toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="font-semibold text-foreground">Click to upload CSV file</p>
                    <p className="text-sm text-muted-foreground">or drag and drop</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-1">Upload Error</h4>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Progress */}
          {uploading && progress && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-foreground">Uploading courses...</span>
                <span className="text-muted-foreground">
                  {progress?.current} / {progress?.total} ({progress?.percentage}%)
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-300 rounded-full"
                  style={{ width: `${progress?.percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{results?.totalProcessed}</div>
                  <div className="text-sm text-blue-800 mt-1">Total Processed</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{results?.successCount}</div>
                  <div className="text-sm text-green-800 mt-1">Successful</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-600">{results?.failureCount}</div>
                  <div className="text-sm text-red-800 mt-1">Failed</div>
                </div>
              </div>

              {results?.failed?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} />
                    Failed Uploads ({results?.failed?.length})
                  </h4>
                  <div className="space-y-2">
                    {results?.failed?.slice(0, 10)?.map((item, idx) => (
                      <div key={idx} className="text-sm text-red-800 border-b border-red-100 pb-1 last:border-0">
                        <span className="font-semibold">Row {item?.rowNumber}</span>
                        {item?.column && (
                          <span className="text-red-600"> · Column: <span className="font-medium">{item?.column}</span></span>
                        )}
                        {item?.value !== undefined && item?.value !== '' && (
                          <span className="text-red-500"> · Value: <span className="font-mono bg-red-100 px-1 rounded">{item?.value}</span></span>
                        )}
                        <span className="block text-red-700 mt-0.5 pl-1">↳ {item?.error}</span>
                      </div>
                    ))}
                    {results?.failed?.length > 10 && (
                      <p className="text-sm text-red-700 italic">
                        ... and {results?.failed?.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleDownloadResults}
                className="w-full"
              >
                Download Detailed Results
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex items-center justify-end gap-3 bg-muted/30">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Courses'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkCourseUploadModal;